import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../config';
import { getDeviceId } from '../device';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useSupportUrl } from '../hooks/useSupportUrl';
import Button from '../components/Button';
import Icon from '../components/Icon';

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { colors } = useTheme();
  const supportUrl = useSupportUrl();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e?: FormEvent) => {
    e?.preventDefault();
    setError('');
    if (!username.trim() || !password.trim()) {
      setError('Please enter your username and password.');
      return;
    }
    setLoading(true);
    try {
      const deviceId = await getDeviceId().catch(() => null);
      const res = await axios.post(
        `${API_BASE}/auth/login`,
        { username: username.trim(), password },
        deviceId ? { headers: { 'X-Device-Id': deviceId } } : undefined,
      );
      const { access_token, refresh_token, user } = res.data;
      await login(access_token, user.id, refresh_token);
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { message?: string } } };
      const serverMsg = e.response?.data?.message ?? '';
      if (e.response?.status === 401) {
        if (typeof serverMsg === 'string' && (serverMsg.toLowerCase().includes('banned') || serverMsg.toLowerCase().includes('suspended'))) {
          setError(serverMsg);
        } else {
          setError('Incorrect username or password.');
        }
      } else if (e.response?.status === 403) {
        setError(serverMsg || 'This action is not allowed.');
      } else {
        setError('Could not connect to the server. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100dvh',
        backgroundColor: colors.secondary,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '24px max(24px, env(safe-area-inset-left))',
      }}
    >
      <div style={{ width: '100%', maxWidth: 420, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div
            style={{
              width: 110,
              height: 110,
              backgroundColor: '#000',
              borderRadius: 20,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 12,
              overflow: 'hidden',
            }}
          >
            <img src="/icons/icon-512.png" alt="" style={{ width: 90, height: 90, objectFit: 'contain' }} />
          </div>
          <div style={{ color: '#f8fafc', fontSize: 30, fontWeight: 900, letterSpacing: 2 }}>AGA</div>
          <div style={{ color: '#94a3b8', fontSize: 13, fontWeight: 500, marginTop: 2 }}>All Game Arena</div>
          <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>Your gaming portal</div>
        </div>

        <form
          onSubmit={handleLogin}
          style={{
            backgroundColor: colors.secondaryRaised,
            borderRadius: 16,
            padding: 22,
            border: `1px solid ${colors.borderStrong}`,
          }}
        >
          <div style={{ fontSize: 20, fontWeight: 700, color: '#f8fafc', marginBottom: 16 }}>Sign In</div>

          {error ? (
            <div
              style={{
                backgroundColor: colors.dangerSoft,
                borderRadius: 8,
                padding: 10,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 14,
                border: `1px solid ${colors.danger}`,
              }}
            >
              <Icon name="exclamation-circle" size={14} color={colors.dangerText} />
              <div style={{ color: colors.dangerText, fontSize: 13, flex: 1 }}>{error}</div>
            </div>
          ) : null}

          <Field label="Username">
            <Icon name="user" size={14} color="#475569" style={{ paddingLeft: 14, paddingRight: 10 }} />
            <input
              style={inputStyle}
              placeholder="Enter your username"
              value={username}
              onChange={e => setUsername(e.target.value)}
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="username"
            />
          </Field>

          <Field label="Password">
            <Icon name="lock" size={14} color="#475569" style={{ paddingLeft: 14, paddingRight: 10 }} />
            <input
              style={{ ...inputStyle, flex: 1 }}
              placeholder="Enter your password"
              type={showPass ? 'text' : 'password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoCapitalize="none"
              autoComplete="current-password"
            />
            <button
              type="button"
              aria-label={showPass ? 'Hide password' : 'Show password'}
              onClick={() => setShowPass(v => !v)}
              style={{
                width: 50,
                minHeight: 50,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon name={showPass ? 'eye-slash' : 'eye'} size={14} color="#475569" />
            </button>
          </Field>

          <Button
            label="Sign In"
            icon="sign-in-alt"
            loading={loading}
            type="submit"
            style={{ marginTop: 22 }}
          />

          <button
            type="button"
            onClick={() => window.open(supportUrl, '_blank', 'noopener')}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              marginTop: 16,
              padding: '8px 0',
              minHeight: 44,
              width: '100%',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: '#94a3b8',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            <Icon name="question-circle" size={13} color="#94a3b8" />
            Forgot password? Contact support
          </button>
        </form>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, marginTop: 28 }}>
          <div style={{ color: '#475569', fontSize: 13 }}>Don't have an account?</div>
          <button
            onClick={() => navigate('/register')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              backgroundColor: '#1e293b',
              padding: '12px 20px',
              borderRadius: 10,
              border: `1px solid ${colors.borderStrong}`,
              minHeight: 50,
              cursor: 'pointer',
              color: colors.accent,
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            <Icon name="user-plus" size={13} color={colors.accent} />
            Create Account - It's Free
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: '#94a3b8',
          marginBottom: 6,
          marginTop: 14,
          letterSpacing: 0.3,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: '#0f172a',
          borderRadius: 8,
          border: '1px solid #334155',
          minHeight: 50,
        }}
      >
        {children}
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  flex: 1,
  padding: '13px 14px 13px 0',
  fontSize: 15,
  color: '#f8fafc',
  background: 'transparent',
  border: 'none',
  outline: 'none',
  width: '100%',
};
